import React, { FC } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import { DeprecatedControlledTextInput } from 'transfix-form/components/ControlledTextInput/ControlledTextInput'
import Box from 'transfix-ui/core/Box'
import { useStorePrices, StorePricesResponse } from 'apps/marketplace/apiTypes/instantRfp'
import {
  StorePricesSchema,
  IStorePricesSchema,
} from 'apps/marketplace/pages/instant-rfp/schema/StorePricesSchema'
import { FormModal } from 'transfix-ui'
import { getUserCookieValues } from 'utils/userAuth'

const { userEmail } = getUserCookieValues()

interface IStorePricesModalProps {
  onSuccess: (toastMessage: string) => void
  onError: (toastMessage: string) => void
  onClose: () => void
}

export const StorePricesModal: FC<IStorePricesModalProps> = ({ onClose, onError, onSuccess }) => {
  const { loading, mutate: submitPrices } = useStorePrices({})
  const methods = useForm({
    resolver: yupResolver(StorePricesSchema),
    defaultValues: {
      googleSheetsUrl: '',
      email: userEmail,
      roundNumber: '',
    },
  })
  const { control, errors, handleSubmit, watch } = methods
  const storePrices = (formParams: IStorePricesSchema) => {
    submitPrices({ rfp: formParams })
      .then((response: StorePricesResponse) => {
        onSuccess(response.message)
        onClose()
      })
      .catch(err => {
        onError(err.data?.errors || 'An unknown error occurred.')
      })
  }

  const values = watch(['googleSheetsUrl', 'email', 'roundNumber'])
  const requiredFieldsFilledOut = !!values.googleSheetsUrl && !!values.email && !!values.roundNumber

  return (
    <FormModal
      open
      title='Store Prices'
      size={5}
      onClose={onClose}
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          dataTest: 'store-prices-modal-dismiss-button',
          action: onClose,
        },
        {
          label: 'Store Prices',
          type: 'confirm',
          dataTest: 'store-prices-modal-submit-button',
          action: handleSubmit(storePrices),
          disabled: !requiredFieldsFilledOut || loading,
        },
      ]}>
      <Box mb={2}>
        <DeprecatedControlledTextInput
          control={control}
          name='googleSheetsUrl'
          TextInputProps={{
            name: 'googleSheetsUrl',
            label: 'Google Sheets URL',
            placeholder: 'Enter URL',
            dataTest: 'google-sheets-url',
            invalid: !!errors.googleSheetsUrl,
            validationMessage: errors.googleSheetsUrl?.message,
            required: true,
          }}
        />
      </Box>
      <Box mb={2}>
        <DeprecatedControlledTextInput
          control={control}
          name='email'
          TextInputProps={{
            name: 'email',
            type: 'email',
            label: 'Email',
            placeholder: 'Enter email',
            dataTest: 'email',
            invalid: !!errors.email,
            validationMessage: errors.email?.message,
            required: true,
          }}
        />
      </Box>
      <Box>
        <DeprecatedControlledTextInput
          control={control}
          name='roundNumber'
          TextInputProps={{
            name: 'roundNumber',
            label: 'Round Number',
            placeholder: 'Enter a round number',
            dataTest: 'round-number',
            invalid: !!errors.roundNumber,
            validationMessage: errors.roundNumber?.message,
            required: true,
          }}
        />
      </Box>
    </FormModal>
  )
}
